/**@flow */

import React, { Fragment } from "react";
import { localize } from "redux-i18n";
import { formatDateTime } from "@transformers/date";
import {
  Button,
  Icons,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  GSGrid,
  SectionComponent,
} from "@components";
import { AddAllergyModal } from "@modules/employee/components";
import { connect } from "react-redux";
import * as employeeSelectors from "@modules/employee/selectors";
import actionCreators from "@modules/employee/actionCreator";
import { toTableSort } from "@transformers/sort";
import type { Allergy as AllergyType } from "../../reducer/medical-details/types";
import type { SortOrder } from "@types";
import type { State } from "@store/reducers";

type PropsFromDispatch = $Exact<{
  onAddAllergyHandler: typeof actionCreators.ToggleAddAllergyModal,
  onDismissAddAllergyHandler: typeof actionCreators.ToggleAddAllergyModal,
  addAllergy: typeof actionCreators.AddAllergy,
  updateAllergy: typeof actionCreators.UpdateAllergy,
  toggleRemoveAllergyModal: typeof actionCreators.ToggleRemoveAllergyModal,
  removeAllergy: typeof actionCreators.RemoveAllergy,
  onDismissRemoveAllergyHandler: typeof actionCreators.ToggleRemoveAllergyModal,
  onSort: typeof actionCreators.SetEmployeeAllergySort,
}>;
type PropsFromState = $Exact<{
  isAddAllergyModalVisible: boolean,
  schema: Object,
  isRemoveAllergyModalVisible: boolean,
  allergyId: number,
  allergyName: string,
  sort: Array<SortOrder> | null,
}>;
type Props = $Exact<{
  ...PropsFromLocalize,
  ...PropsFromDispatch,
  ...PropsFromState,
  allergies: AllergyType[],
  employeeId: number,
  clientId: number,
  onPageChange: (page: number) => void,
}>;

export class Allergies extends React.Component<Props> {
  removeAllergy = () =>
    this.props.removeAllergy({
      allergyId: this.props.allergyId,
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });

  onAddAllergy = ({ formData }: Object) =>
    this.props.addAllergy({
      formData,
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });

  onToggleRemoveAllergyModal = (id: number) => () =>
    this.props.toggleRemoveAllergyModal({
      allergyId: id,
      visible: true,
    });

  onSort = (sort: $Exact<{ id: string | null, direction: "asc" | "desc" }>) => {
    this.props.onSort({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      sort,
    });
  };

  handleChangeStatus = (status: string, allergy: AllergyType) => () => {
    this.props.updateAllergy({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      data: {
        status,
        id: allergy.id,
        allergy: allergy.allergy,
        notes: allergy.notes,
      },
      allergyDetailsId: allergy.id,
    });
  };

  get columns() {
    return [
      {
        name: this.props.t("ID"),
        renderCell: ({ row }: { row: Object }) => row.allergy.id,
        key: "id",
        sortable: true,
      },
      {
        name: this.props.t("ALLERGY"),
        renderCell: ({ row }: { row: Object }) => row.allergy.name,
        key: "allergy",
        sortable: true,
      },
      {
        name: this.props.t("MEDICAL_DETAILS_TABLE_TITLE_NOTES"),
        renderCell: ({ row }: { row: Object }) => row.notes,
        key: "notes",
      },
      {
        name: this.props.t("MEDICAL_DETAILS_TABLE_TITLE_STATUS"),
        renderCell: ({ row }: { row: Object }) => this.props.t(row.status),
        key: "status",
      },
      {
        name: this.props.t("DATE_RECORDED"),
        renderCell: ({ row }: { row: Object }) => formatDateTime(row.createdAt),
        key: "createdAt",
        sortable: true,
      },
      {
        name: this.props.t("RECORDED_BY"),
        renderCell: ({ row }: { row: Object }) =>
          `${row.updatedBy.firstName} ${row.updatedBy.lastName}`,
        key: "recordedBy",
      },
      {
        name: this.props.t("ASSOCIATED_ABSENCE"),
        renderCell: ({ row }: { row: Object }) =>
          row.absenceId || this.props.t("NOT_ASSOCIATED_ABSENCE"),
        key: "associatedAbsence",
      },
      {
        name: "",
        key: "update",
        renderCell: ({ row }: { row: AllergyType }) => {
          if (row.status === "ACTIVE") {
            return (
              <Button
                size="small"
                className="text-nowrap"
                onClick={this.handleChangeStatus("INACTIVE", row)}
              >
                {this.props.t("INACTIVE")}
              </Button>
            );
          }
          return (
            <Button
              size="small"
              className="text-nowrap"
              onClick={this.handleChangeStatus("ACTIVE", row)}
            >
              {this.props.t("ACTIVE")}
            </Button>
          );
        },
      },
      {
        name: "",
        key: "actions",
        renderCell: ({ row }: { row: AllergyType }) => {
          return (
            <span
              className="material-icons pointer font-size-26"
              onClick={this.onToggleRemoveAllergyModal(row.id)}
            >
              delete
            </span>
          );
        },
      },
    ];
  }

  render() {
    return (
      <Fragment>
        <SectionComponent
          title={<h5>{this.props.t("ALLERGIES")}</h5>}
          wrapperClassName="flex-column"
          rightLink={
            <Button materialIcon="add_box" onClick={this.props.onAddAllergyHandler}>
              {this.props.t("ADD")}
            </Button>
          }
        >
          <GSGrid
            className="pb-5"
            row={this.props.allergies}
            column={this.columns}
            onSortChange={this.onSort}
            sort={toTableSort(this.props.sort)}
          />
        </SectionComponent>
        {this.props.schema && this.props.isAddAllergyModalVisible && (
          <AddAllergyModal
            onDismissHandler={this.props.onDismissAddAllergyHandler}
            onSubmit={this.onAddAllergy}
            schema={this.props.schema}
          />
        )}
        {this.props.isRemoveAllergyModalVisible && (
          <Modal className="modal-small">
            <ModalHeader toggle={this.props.onDismissRemoveAllergyHandler}>
              {this.props.t("ARE_YOU_SURE")}
            </ModalHeader>
            <ModalBody>
              {this.props.t("REMOVE_CONFIRMATION", {
                item: <strong>{this.props.allergyName},</strong>,
              })}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={this.props.onDismissRemoveAllergyHandler}>
                {this.props.t("CANCEL")}
              </Button>
              <Button onClick={this.removeAllergy}>{this.props.t("REMOVE_ALLERGY")}</Button>
            </ModalFooter>
          </Modal>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  const allergyId = employeeSelectors.getAllergyId(state);

  return {
    isAddAllergyModalVisible: employeeSelectors.getIsAddAllergyModalVisible(state),
    schema: employeeSelectors.getAllergySchema(state),
    isRemoveAllergyModalVisible: employeeSelectors.getIsRemoveAllergyModalVisible(state),
    allergyId,
    allergyName: employeeSelectors.getAllergyItemName(allergyId)(state),
    sort: employeeSelectors.getEmployeeAllergySort(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  onAddAllergyHandler: () => actionCreators.ToggleAddAllergyModal(true),
  onDismissAddAllergyHandler: () => actionCreators.ToggleAddAllergyModal(false),
  addAllergy: actionCreators.AddAllergy,
  updateAllergy: actionCreators.UpdateAllergy,
  toggleRemoveAllergyModal: actionCreators.ToggleRemoveAllergyModal,
  removeAllergy: actionCreators.RemoveAllergy,
  onDismissRemoveAllergyHandler: () => actionCreators.ToggleRemoveAllergyModal({ visible: false }),
  onSort: actionCreators.SetEmployeeAllergySort,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(Allergies));
